import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../api";
import { homeFor } from "../components/ProtectedRoute";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

// Mercury internal devs can append ?dev=mercury to see this panel. Public visitors and
// clients (incl. Avni) never see Mercury admin credentials exposed on the login page.
const DEV_CREDENTIALS = [
  { label: "Mercury Admin (full access)", email: "admin@vi-assessment.com", password: "Admin@123", role: "admin" },
  { label: "VI Sales Lead (client view)", email: "salesleader@vodafoneidea.com", password: "Vi@SalesLead123", role: "client_lead" },
  { label: "Participant (L1, North)", email: "arjun.reddy@vi.com", password: "Welcome@123", role: "participant" },
  { label: "Manager (North)", email: "rajesh.manager@vi.com", password: "Welcome@123", role: "manager" },
  { label: "Channel Partner", email: "anil.partner@channelco.com", password: "Welcome@123", role: "channel_partner" },
];

// Pre-fillable demo logins triggered via ?demo=... query string. Only the public-facing
// demo accounts live here; never the admin.
const DEMO_PREFILL = {
  candidate: { email: "demo.candidate@vi.com", password: "Demo@COMPASS2026", label: "Demo participant · Aanya Sharma" },
};

export default function Login() {
  const [params] = useSearchParams();
  const devMode = params.get("dev") === "mercury";
  const demoKey = params.get("demo");
  const demoPrefill = demoKey && DEMO_PREFILL[demoKey];

  const [email, setEmail] = useState(demoPrefill?.email || "");
  const [password, setPassword] = useState(demoPrefill?.password || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // If the URL carries ?demo=candidate, prefill the form and surface a friendly banner.
  useEffect(() => {
    if (demoPrefill) {
      setEmail(demoPrefill.email);
      setPassword(demoPrefill.password);
    }
  }, [demoPrefill]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email, password);
      navigate(homeFor(u.role));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (d) => { setEmail(d.email); setPassword(d.password); };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-white">
      {/* Left brand panel — VI aubergine identity surface. Panel bg matches
          the logo image's baked-in aubergine (#5E004C) EXACTLY so the logo
          sits on the surface with no visible edge. Decorative overlays
          removed — they were tinting the surrounding aubergine and creating
          a "box behind the logo" effect. */}
      <div className="lg:col-span-2 relative overflow-hidden hidden lg:block">
        <div className="absolute inset-0 bg-[#5E004C]" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div>
            {/* Primary logo — aubergine-bg variant, blends into panel */}
            <div data-testid="login-brand-logo">
              <img src="/brand/vi-academy-logo-aubergine.png" alt="Vi Business Academy" className="h-24 w-auto object-contain" />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-white/60 font-semibold">
              Powered by <span className="text-[#FFC600]">Mercury Compass Assess</span>
            </div>
          </div>
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-[10px] uppercase tracking-widest text-[#FFC600] font-bold">
              <Sparkles size={12} /> Certification Programme
            </div>
            <h1 className="text-4xl lg:text-5xl font-heading font-black leading-[1.05] tracking-tight">
              Vi Business Academy <span className="text-[#FFC600]">Certified Sales Consultant</span>
            </h1>
            <p className="text-white/75 text-base max-w-md leading-relaxed">
              The certification comprises three components — a knowledge-based assessment, a customer sales pitch simulation, and an AI-led role play — designed to evaluate how effectively you apply consultative selling principles in realistic customer scenarios.
            </p>
            <div className="flex items-center gap-2 text-white/75 text-sm">
              <ShieldCheck size={16} className="text-[#FFC600]" />
              <span>ID + face verified · AI voice · AI-scored responses</span>
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-semibold">
            Sign in to begin your certification
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-3 flex items-start lg:items-center justify-center p-6 lg:p-16 grid-bg min-h-screen">
        <div className="w-full max-w-md fade-in">
          {/* Mobile-only brand strip — matches the desktop aubergine panel exactly */}
          <div className="lg:hidden -mx-6 -mt-6 mb-6 p-5 bg-[#5E004C] text-white">
            <img src="/brand/vi-academy-logo-aubergine.png" alt="Vi Business Academy" className="h-16 w-auto object-contain" />
            <div className="mt-3 pt-3 border-t border-white/10 text-[10.5px] text-white/75 leading-relaxed">
              <span className="text-[#FFC600] font-semibold">Vi Business Academy - Certified Sales Consultant</span> · Powered by Mercury Compass Assess
            </div>
          </div>

          <div className="overline mb-4">SECURE SIGN-IN</div>
          <h2 className="text-3xl font-heading font-black tracking-tight mb-2">Welcome back.</h2>
          <p className="text-[#525252] text-sm mb-10">Sign in with the credentials Vi Business Academy sent you. Your certification dashboard appears the moment you sign in.</p>

          {demoPrefill && (
            <div className="mb-6 p-4 border-l-4 border-[#EE2737] bg-[#FFF5F5] flex items-start gap-3" data-testid="demo-prefill-banner">
              <Sparkles size={18} className="text-[#EE2737] flex-shrink-0 mt-0.5" />
              <div className="text-[13px] text-[#0A0A0A] leading-relaxed">
                <b>Demo evaluation login</b> — your credentials are pre-filled. Click <em>Sign in</em> to walk through the AI-proctored participant journey.
                <div className="text-[11px] text-[#525252] mt-1">Signed in as: <code className="font-bold">{demoPrefill.email}</code></div>
              </div>
            </div>
          )}

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#525252] mb-2">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="input" placeholder="name@vi.com" data-testid="login-email"
                autoFocus={!demoPrefill}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-[#525252] mb-2">Password</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="input" placeholder="••••••••" data-testid="login-password"
                autoFocus={!!demoPrefill}
              />
            </div>
            {error && (
              <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn btn-primary w-full justify-center !py-3" data-testid="login-submit">
              {loading ? "Signing in…" : (<>Sign in <ArrowRight size={16} /></>)}
            </button>
          </form>

          {/* Dev panel — only visible when ?dev=mercury is in the URL */}
          {devMode && (
            <div className="mt-10 pt-6 border-t border-[#E5E7EB]" data-testid="dev-credentials">
              <div className="overline mb-3 !text-[#EE2737]">MERCURY DEV · CLICK TO FILL</div>
              <p className="text-[11px] text-[#878787] mb-3">Visible only with <code>?dev=mercury</code>. Do not share this URL externally.</p>
              <div className="grid grid-cols-1 gap-2">
                {DEV_CREDENTIALS.map((d) => (
                  <button
                    key={d.email}
                    onClick={() => fillDemo(d)}
                    type="button"
                    className="text-left card !rounded-sm p-3 flex items-center justify-between group"
                    data-testid={`demo-${d.role}`}
                  >
                    <div>
                      <div className="text-[13px] font-semibold text-[#0A0A0A]">{d.label}</div>
                      <div className="text-[11px] text-[#878787] mt-0.5">{d.email}</div>
                    </div>
                    <ArrowRight size={14} className="text-[#878787] group-hover:text-[#EE2737]" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
